import { supabase } from '@/integrations/supabase/client';
import { NoteType } from '@/types/notes';

export interface NoteSpace {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface FlowNode {
  id: string;
  note_space_id: string;
  node_type: string;
  position_x: number;
  position_y: number;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface FlowEdge {
  id: string;
  note_space_id: string;
  source: string;
  target: string;
  edge_type?: string;
  data?: Record<string, any>;
  created_at: string;
}

export interface WorkspaceShare {
  id: string;
  note_space_id: string;
  shared_with_user_id: string;
  permission_level: string;
  created_at: string;
}

// Note Space operations
export const noteSpaceService = {
  async create(name: string = 'Untitled Interview'): Promise<NoteSpace> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('note_spaces')
      .insert({ name, user_id: user.id })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getAll(): Promise<NoteSpace[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('note_spaces')
      .select('*')
      .order('updated_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<NoteSpace | null> {
    const { data, error } = await supabase
      .from('note_spaces')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<Pick<NoteSpace, 'name'>>): Promise<NoteSpace> {
    const { data, error } = await supabase
      .from('note_spaces')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('note_spaces')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Flow Node operations
export const flowNodeService = {
  async create(noteSpaceId: string, nodeData: {
    node_type: string;
    position_x: number;
    position_y: number;
    data: Record<string, any>;
  }): Promise<FlowNode> {
    const { data, error } = await supabase
      .from('flow_nodes')
      .insert({
        note_space_id: noteSpaceId,
        ...nodeData
      })
      .select()
      .single();
    
    if (error) throw error;
    return {
      ...data,
      data: data.data as Record<string, any>
    };
  },

  async getByNoteSpaceId(noteSpaceId: string): Promise<FlowNode[]> {
    const { data, error } = await supabase
      .from('flow_nodes')
      .select('*')
      .eq('note_space_id', noteSpaceId)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    return (data || []).map(node => ({
      ...node,
      data: node.data as Record<string, any>
    }));
  },

  async update(id: string, updates: Partial<Pick<FlowNode, 'position_x' | 'position_y' | 'data'>>): Promise<FlowNode> {
    const { data, error } = await supabase
      .from('flow_nodes')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return {
      ...data,
      data: data.data as Record<string, any>
    };
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('flow_nodes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Flow Edge operations
export const flowEdgeService = {
  async create(noteSpaceId: string, edgeData: {
    source: string;
    target: string;
    edge_type?: string;
    data?: Record<string, any>;
  }): Promise<FlowEdge> {
    const { data, error } = await supabase
      .from('flow_edges')
      .insert({
        note_space_id: noteSpaceId,
        ...edgeData
      })
      .select()
      .single();
    
    if (error) throw error;
    return {
      ...data,
      data: data.data as Record<string, any>
    };
  },

  async getByNoteSpaceId(noteSpaceId: string): Promise<FlowEdge[]> {
    const { data, error } = await supabase
      .from('flow_edges')
      .select('*')
      .eq('note_space_id', noteSpaceId)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    return (data || []).map(edge => ({
      ...edge,
      data: edge.data as Record<string, any>
    }));
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('flow_edges')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Workspace Sharing operations
export const workspaceShareService = {
  async create(noteSpaceId: string, userEmail: string, permissionLevel: 'view' | 'edit' = 'view'): Promise<WorkspaceShare> {
    // First get the user ID by email
    const { data: userId, error: userError } = await supabase
      .rpc('get_user_id_by_email', { user_email: userEmail });
    
    if (userError || !userId) {
      throw new Error('User not found with that email address');
    }

    const { data, error } = await supabase
      .from('workspace_shares')
      .insert({
        note_space_id: noteSpaceId,
        shared_with_user_id: userId,
        permission_level: permissionLevel
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getByNoteSpaceId(noteSpaceId: string): Promise<Array<WorkspaceShare & { user_email: string }>> {
    const { data, error } = await supabase
      .from('workspace_shares')
      .select('*')
      .eq('note_space_id', noteSpaceId);
    
    if (error) throw error;
    
    // Get user emails for each share
    const sharesWithEmails = await Promise.all(
      (data || []).map(async (share) => {
        const { data: email } = await supabase
          .rpc('get_user_email_by_id', { user_uuid: share.shared_with_user_id });
        
        return {
          ...share,
          user_email: email || 'Unknown user'
        };
      })
    );
    
    return sharesWithEmails;
  },

  async delete(shareId: string): Promise<void> {
    const { error } = await supabase
      .from('workspace_shares')
      .delete()
      .eq('id', shareId);
    
    if (error) throw error;
  },

  async updatePermission(shareId: string, permissionLevel: 'view' | 'edit'): Promise<WorkspaceShare> {
    console.log('=== DATABASE UPDATE START ===');
    console.log('Updating share ID:', shareId, 'to permission:', permissionLevel);
    
    const { data, error } = await supabase
      .from('workspace_shares')
      .update({ permission_level: permissionLevel })
      .eq('id', shareId)
      .select()
      .single();
    
    console.log('Raw database response:', { data, error });
    
    if (error) {
      console.error('Database error:', error);
      throw error;
    }
    
    if (!data) {
      console.error('No data returned from update');
      throw new Error('Failed to update permission - no data returned');
    }
    
    console.log('Update successful, returning data:', data);
    console.log('=== DATABASE UPDATE END ===');
    
    return data;
  }
};

// Utility functions for converting between React Flow and Supabase formats
export const convertFlowNodeToReactFlow = (node: FlowNode) => ({
  id: node.id,
  type: node.node_type === 'stickyNote' ? 'note' : node.node_type, // Map stickyNote to note for React Flow
  position: { x: node.position_x, y: node.position_y },
  data: node.data
});

export const convertFlowEdgeToReactFlow = (edge: FlowEdge) => ({
  id: edge.id,
  source: edge.source,
  target: edge.target,
  type: edge.edge_type || 'default',
  data: edge.data || {}
});
